import { translate } from '../../../lib/translations';

interface PlayerTradeInfoProps {
  isGoalie?: boolean;
  name: string | null;
  goals?: number | null;
  assists?: number | null;
  points?: number | null;
  wins?: number | null;
  savepct?: number | null;
  shutouts?: number | null;
  salary: number | null;
  yearleft: string | null;
  isNHL?: boolean | null;
  isScratch?: boolean;
  salaryRetainPct?: number | null;
  language: 'en' | 'fr';
}

export function PlayerTradeInfo({
  isGoalie = false,
  name,
  goals,
  assists,
  points,
  wins,
  savepct,
  shutouts,
  salary,
  yearleft,
  isNHL,
  isScratch = false,
  salaryRetainPct,
  language,
}: PlayerTradeInfoProps) {
  if (!name) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const hasRetention = salaryRetainPct && salaryRetainPct > 0;
  const retainedAmount = hasRetention && salary ? (salary * salaryRetainPct) / 100 : 0;
  const effectiveSalary = hasRetention && salary ? salary - retainedAmount : (salary || 0);

  return (
    <div className="list-group-item">
      <div className="d-flex align-items-center gap-2 mb-1">
        <h6 className="mb-0">{name}</h6>
        {isNHL !== null && (
          <span className={`badge ${
            isScratch 
              ? 'bg-secondary' 
              : isNHL 
                ? 'bg-primary' 
                : 'bg-warning text-dark'
          }`}>
            {isScratch 
              ? translate('scratch.player', language)
              : isNHL 
                ? translate('main.club', language) 
                : translate('reserves', language)
            }
          </span>
        )}
        {hasRetention && (
          <span className="badge bg-info text-dark">
            {language === 'fr' ? 'Retenue' : 'Retention'}: {salaryRetainPct}%
          </span>
        )}
      </div>
      <div className="small text-muted mb-2">
        <span className="me-3">
          {translate('salary', language)}: {salary ? formatCurrency(salary) : 'N/A'}
          {hasRetention && (
            <span className="d-block text-info" style={{ fontSize: '0.85em' }}>
              {language === 'fr' ? 'Retenu' : 'Retained'}: {formatCurrency(retainedAmount)} ({salaryRetainPct}%)
              <br />
              {language === 'fr' ? 'Effectif' : 'Effective'}: {formatCurrency(effectiveSalary)}
            </span>
          )}
        </span>
        <span>{translate('contract', language)}: {yearleft || 'N/A'} {translate('years', language)}</span>
      </div>
      <div className="d-flex gap-3 small">
        {isGoalie ? (
          <>
            <span>{translate('wins', language)}: {wins || 0}</span>
            <span>{translate('save.percentage', language)}: {savepct?.toFixed(3) || '0.000'}</span>
            <span>{translate('shutouts', language)}: {shutouts || 0}</span>
          </>
        ) : (
          <>
            <span>{translate('goals', language)}: {goals || 0}</span>
            <span>{translate('assists', language)}: {assists || 0}</span>
            <span>{translate('points', language)}: {points || 0}</span>
          </>
        )}
      </div>
    </div>
  );
}